// ForwardsflowPL.js — Forwardsflow KES P&L: realised FX fee, accrued costs, forward MTM, total.
// Separates realised vs accrued items.

import React, { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';

function fmtKES(value) {
  if (value == null) return '--';
  const num = parseFloat(value);
  const sign = num < 0 ? '-' : '';
  const abs = Math.abs(num);
  return `${sign}KES ${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function colorClass(value) {
  if (value == null) return 'text-zinc-500';
  const num = parseFloat(value);
  if (num > 0.005) return 'text-emerald-600';
  if (num < -0.005) return 'text-red-600';
  return 'text-zinc-700';
}

const REALISED_LINES = [
  { key: 'fx_conversion_fee', label: 'FX Conversion Fee (Realised)' },
  { key: 'swift_rebate', label: 'SWIFT Fee Rebate (Realised)' },
];

const ACCRUED_LINES = [
  { key: 'accrued_funding_cost', label: 'Funding Cost (Accrued)' },
  { key: 'accrued_operational_cost', label: 'Operational Cost (Accrued)' },
  { key: 'forward_mtm', label: 'Forward MTM (Unrealised)' },
];

export default function ForwardsflowPL({ latestMark, events = [] }) {
  // Aggregate realised P&L from events
  const realisedPL = useMemo(() => {
    const result = { fx_conversion_fee: 0, swift_rebate: 0 };
    if (!events || events.length === 0) return result;

    events.forEach(evt => {
      if (evt.party === 'forwardsflow') {
        if (evt.event_type === 'fx_conversion_fee' || evt.event_type === 'fx_fee') {
          result.fx_conversion_fee += parseFloat(evt.amount_kes || 0);
        }
        if (evt.event_type === 'swift_rebate') {
          result.swift_rebate += parseFloat(evt.amount_kes || 0);
        }
      }
    });
    return result;
  }, [events]);

  // Accrued values from latest mark
  const accruedPL = useMemo(() => {
    if (!latestMark) return { accrued_funding_cost: null, accrued_operational_cost: null, forward_mtm: null };
    return {
      accrued_funding_cost: latestMark.ff_funding_cost_kes ?? null,
      accrued_operational_cost: latestMark.ff_operational_cost_kes ?? null,
      forward_mtm: latestMark.ff_forward_mtm_kes ?? null,
    };
  }, [latestMark]);

  // Compute totals
  const realisedTotal = useMemo(() => {
    return realisedPL.fx_conversion_fee + realisedPL.swift_rebate;
  }, [realisedPL]);

  const accruedTotal = useMemo(() => {
    const funding = parseFloat(accruedPL.accrued_funding_cost || 0);
    const ops = parseFloat(accruedPL.accrued_operational_cost || 0);
    const mtm = parseFloat(accruedPL.forward_mtm || 0);
    return funding + ops + mtm;
  }, [accruedPL]);

  const grandTotal = realisedTotal + accruedTotal;

  const allEmpty = events.length === 0 && !latestMark;

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4" style={{ color: '#4D4D4D' }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Forwardsflow P&L (KES)
        </span>
      </div>

      {allEmpty ? (
        <p className="font-mono text-sm text-zinc-400">
          No P&L data available yet.
        </p>
      ) : (
        <>
          {/* Realised section */}
          <div className="mb-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2 border-b border-zinc-200 pb-1">
              Realised
            </p>
            <table className="w-full text-xs border-collapse">
              <tbody>
                {REALISED_LINES.map(line => {
                  const val = realisedPL[line.key];
                  return (
                    <tr key={line.key} className="border-b border-zinc-100">
                      <td className="py-1.5 px-2 font-mono text-zinc-700">{line.label}</td>
                      <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${colorClass(val)}`}>
                        {fmtKES(val)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-b border-zinc-300 bg-zinc-50">
                  <td className="py-1.5 px-2 font-mono text-zinc-900 font-semibold">
                    Realised Subtotal
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono tabular-nums font-semibold ${colorClass(realisedTotal)}`}>
                    {fmtKES(realisedTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Accrued section */}
          <div className="mb-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2 border-b border-zinc-200 pb-1">
              Accrued / Unrealised
            </p>
            <table className="w-full text-xs border-collapse">
              <tbody>
                {ACCRUED_LINES.map(line => {
                  const val = accruedPL[line.key];
                  return (
                    <tr key={line.key} className="border-b border-zinc-100">
                      <td className="py-1.5 px-2 font-mono text-zinc-700">{line.label}</td>
                      <td className={`py-1.5 px-2 text-right font-mono tabular-nums ${colorClass(val)}`}>
                        {fmtKES(val)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-b border-zinc-300 bg-zinc-50">
                  <td className="py-1.5 px-2 font-mono text-zinc-900 font-semibold">
                    Accrued Subtotal
                  </td>
                  <td className={`py-1.5 px-2 text-right font-mono tabular-nums font-semibold ${colorClass(accruedTotal)}`}>
                    {fmtKES(accruedTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Grand total */}
          <div className="border border-zinc-200 p-3" style={{ borderColor: '#4D4D4D' }}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                Total P&L
              </span>
              <span className={`font-mono text-lg font-bold tabular-nums ${colorClass(grandTotal)}`}>
                {fmtKES(grandTotal)}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="font-mono text-[9px] text-zinc-400">
                Realised: {fmtKES(realisedTotal)}
              </span>
              <span className="font-mono text-[9px] text-zinc-400">
                Accrued: {fmtKES(accruedTotal)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
